import React, { useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { Card, Descriptions, Tag, Button, message, Spin, Alert } from "antd";
import useLandlordApplications from "./hooks/useApplications";
import { UserContext } from "../../../context/UserContext";
import LeaseForm from "./components/LeaseForm";
import { ref, get, update } from "firebase/database";
import { rtdb } from "../../../firebase.config";

const ApplicationDetails = () => {
  const { applicationId } = useParams();
  const { user } = useContext(UserContext);
  const landlordUid = user?.uid;
  const { rows, loading, error } = useLandlordApplications(landlordUid);
  const [showLeaseForm, setShowLeaseForm] = useState(false);

  const application = rows.find(
    (r) => r.id === applicationId || r.raw.applicationId === applicationId
  )?.raw;

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", marginTop: 50 }}>
        <Spin tip="Loading application..." />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        message="Failed to load applications"
        description={String(error)}
      />
    );
  }

  if (!application) {
    return <Alert type="warning" message="Application not found" />;
  }

  const renderTag = (val) =>
    val ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>;

  const handleLeaseStatusUpdate = async (applicationId, newStatus) => {
    try {
      const leaseRef = ref(rtdb, `leases/${applicationId}`);
      const snap = await get(leaseRef);

      if (!snap.exists()) {
        message.error("No lease found for this application.");
        return;
      }

      const lease = snap.val();

      if (
        (lease.status === "sent" && newStatus === "sent") ||
        (lease.status === "rejected" && newStatus === "rejected")
      ) {
        message.warning(`Lease has already been ${newStatus}.`);
        return;
      }

      await update(leaseRef, {
        status: newStatus,
        updatedAt: Date.now(),
      });

      message.success(`Lease ${newStatus} successfully`);
    } catch (err) {
      console.error("Lease save failed:", err);
      message.error("Could not update lease. Please try again.");
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <h2>Application Details</h2>

      {/* Tenant Info */}
      <Card title="Tenant Information" style={{ marginBottom: 16 }}>
        <Descriptions column={2} bordered>
          <Descriptions.Item label="Name">
            {application.applicantName}
          </Descriptions.Item>
          <Descriptions.Item label="Date of Birth">
            {application.dateOfBirth}
          </Descriptions.Item>
          <Descriptions.Item label="Email">
            {application.email || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Phone">
            {application.phone}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color="orange">{application.applicationStatus}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Application Date">
            {application.applicationDate}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Property Info */}
      <Card title="Property Information" style={{ marginBottom: 16 }}>
        <Descriptions column={2} bordered>
          <Descriptions.Item label="Property">
            {application.propertyTitle}
          </Descriptions.Item>
          <Descriptions.Item label="Property ID">
            {application.propertyId}
          </Descriptions.Item>
          <Descriptions.Item label="Rent">
            ${application.rent}
          </Descriptions.Item>
          <Descriptions.Item label="Landlord">
            {application.landlordId}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Current Residence */}
      <Card title="Current Residence" style={{ marginBottom: 16 }}>
        <Descriptions column={2} bordered>
          <Descriptions.Item label="Address">
            {application.currentAddress}
          </Descriptions.Item>
          <Descriptions.Item label="City">
            {application.currentCity}
          </Descriptions.Item>
          <Descriptions.Item label="State">
            {application.currentState}
          </Descriptions.Item>
          <Descriptions.Item label="Zip">
            {application.currentZip}
          </Descriptions.Item>
          <Descriptions.Item label="Residence Type">
            {application.currentResidenceType}
          </Descriptions.Item>
          <Descriptions.Item label="Duration">
            {application.currentDuration}
          </Descriptions.Item>
          <Descriptions.Item label="Monthly Payment">
            {application.currentMonthlyPayment}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Previous Residence */}
      {application.previousAddress && (
        <Card title="Previous Residence" style={{ marginBottom: 16 }}>
          <Descriptions column={2} bordered>
            <Descriptions.Item label="Address">
              {application.previousAddress}
            </Descriptions.Item>
            <Descriptions.Item label="City">
              {application.previousCity}
            </Descriptions.Item>
            <Descriptions.Item label="State">
              {application.previousState}
            </Descriptions.Item>
            <Descriptions.Item label="Zip">
              {application.previousZip}
            </Descriptions.Item>
            <Descriptions.Item label="Duration">
              {application.previousDuration}
            </Descriptions.Item>
            <Descriptions.Item label="Monthly Payment">
              {application.previousMonthlyPayment}
            </Descriptions.Item>
            {/* <Descriptions.Item label="Landlord Name">
              {application.previousLandlordName}
            </Descriptions.Item>
            <Descriptions.Item label="Landlord Phone">
              {application.previousLandlordPhone}
            </Descriptions.Item>
            <Descriptions.Item label="Reason for Leaving">
              {application.previousLandlordReason}
            </Descriptions.Item> */}
          </Descriptions>
        </Card>
      )}

      {/* Employment Info */}
      <Card title="Employment Information" style={{ marginBottom: 16 }}>
        <Descriptions column={2} bordered>
          <Descriptions.Item label="Employer">
            {application.employer}
          </Descriptions.Item>
          {/* <Descriptions.Item label="Title">
            {application.employerTitle}
          </Descriptions.Item> */}
          <Descriptions.Item label="Phone">
            {application.employerPhone}
          </Descriptions.Item>
          <Descriptions.Item label="Email">
            {application.employerEmail}
          </Descriptions.Item>
          <Descriptions.Item label="Address">
            {application.employerAddress}
          </Descriptions.Item>
          <Descriptions.Item label="Duration">
            {application.employmentDuration}
          </Descriptions.Item>
          <Descriptions.Item label="Salary Type">
            {application.salaryType}
          </Descriptions.Item>
          <Descriptions.Item label="Annual Income">
            ${application.annualIncome}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Emergency Contact */}
      <Card title="Emergency Contact" style={{ marginBottom: 16 }}>
        <Descriptions column={2} bordered>
          <Descriptions.Item label="Name">
            {application.emergencyName}
          </Descriptions.Item>
          <Descriptions.Item label="Phone">
            {application.emergencyPhone}
          </Descriptions.Item>
          <Descriptions.Item label="Relationship">
            {application.emergencyRelationship}
          </Descriptions.Item>
          <Descriptions.Item label="Address">
            {application.emergencyAddress}
          </Descriptions.Item>
          <Descriptions.Item label="City">
            {application.emergencyCity}
          </Descriptions.Item>
          <Descriptions.Item label="State">
            {application.emergencyState}
          </Descriptions.Item>
          <Descriptions.Item label="Zip">
            {application.emergencyZip}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* References */}
      {application.referenceName && (
        <Card title="References" style={{ marginBottom: 16 }}>
          <Descriptions column={2} bordered>
            <Descriptions.Item label="Reference 1">
              {application.referenceName} ({application.referencePhone})
            </Descriptions.Item>
            <Descriptions.Item label="Reference 2">
              {application.referenceName2} ({application.referencePhone2})
            </Descriptions.Item>
            <Descriptions.Item label="Reference 3">
              {application.referenceName3} ({application.referencePhone3})
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {/* Reports */}
      <Card title="Reports & Checks" style={{ marginBottom: 16 }}>
        <Descriptions column={2} bordered>
          <Descriptions.Item label="Credit Report">
            {renderTag(application.creditReport)}
          </Descriptions.Item>
          <Descriptions.Item label="Criminal Report">
            {renderTag(application.criminalReport)}
          </Descriptions.Item>
          <Descriptions.Item label="Bankruptcy">
            {renderTag(application.bankruptcy)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Actions */}
      <div style={{ marginTop: 20 }}>
        <Button
          type="default"
          style={{ marginRight: 10 }}
          onClick={() => setShowLeaseForm(true)}
        >
          Add / Edit Lease
        </Button>

        <Button
          type="primary"
          style={{ marginRight: 10 }}
          onClick={() => handleLeaseStatusUpdate(applicationId, "sent")}
        >
          Send Lease
        </Button>

        <Button
          danger
          onClick={() => handleLeaseStatusUpdate(applicationId, "rejected")}
        >
          Reject
        </Button>
      </div>

      {/* Lease form modal */}
      <LeaseForm
        visible={showLeaseForm}
        onClose={() => setShowLeaseForm(false)}
        application={application}
      />
    </div>
  );
};

export default ApplicationDetails;
